import ArrowSearch from '@mdi/svg/svg/arrow-right.svg?react';
import {
  type EventSpotCheckTemplate,
  type SpotCheckTemplate,
} from '@solinkcorp/event-standard/lib/src/types';
import settings from '@solinkcorp/settings';
import { MDIIcon } from '@solinkcorp/solink-components';
import { ScheduledActivityRuleType } from '@solinkcorp/solink-schedule';
import { type AiNotification, type ScheduledActivityEntity } from 'clients/CallhomeApiTypes';
import React from 'react';
import useLocale from 'src/hooks/useLocale';
import { useSelector } from 'stores/store';
import { type SpotCheckView } from '../../SpotCheckMainContent';
import SpotCheckCard from '../SpotCheckCard/SpotCheckCard';
import styles from './SpotCheckTemplates.module.sass';

interface SpotCheckTemplatesProps {
  setView: React.Dispatch<React.SetStateAction<SpotCheckView>>;
  title: string;
  templates: SpotCheckTemplate[] | EventSpotCheckTemplate[];
  setSpotCheck: React.Dispatch<React.SetStateAction<ScheduledActivityEntity | undefined>>;
  setEventSpotCheck: React.Dispatch<React.SetStateAction<AiNotification | undefined>>;
  spotCheckSearchPrompt: string;
  setSpotCheckSearchPrompt: React.Dispatch<React.SetStateAction<string>>;
  eventSpotCheckSearchPrompt: string;
  setEventSpotCheckSearchPrompt: React.Dispatch<React.SetStateAction<string>>;
  selectedCategoryKey: string;
  setFormMode: React.Dispatch<React.SetStateAction<'create' | 'edit'>>;
  setIsExistingSpotCheck: React.Dispatch<React.SetStateAction<boolean>>;
}
export default function SpotCheckTemplates(props: SpotCheckTemplatesProps) {
  const {
    setView,
    title,
    templates,
    setSpotCheck,
    setEventSpotCheck,
    setSpotCheckSearchPrompt,
    setEventSpotCheckSearchPrompt,
    selectedCategoryKey,
    setFormMode,
    setIsExistingSpotCheck,
  } = props;

  const locale = useLocale();
  const { spotCheckMode } = useSelector((state) => ({
    spotCheckMode: state.filter.spotCheckMode,
  }));
  const isEventSpotCheckMode = spotCheckMode === 'event';

  const language = locale.locale;

  const [spotCheckSearch, setSpotCheckSearch] = React.useState('');

  const handleSpotCheckSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSpotCheckSearch(event.target.value);
  };

  const handleTemplateClick = (template: SpotCheckTemplate | EventSpotCheckTemplate) => {
    const { id, i18n, ...restOfTemplate } = template;
    const scheduledActivitySpotCheck = !isEventSpotCheckMode
      ? {
          ...restOfTemplate,
          ruleType: ScheduledActivityRuleType.CRON,
          description: i18n[language].description ?? template.description,
          title: i18n[language].title ?? template.title,
          metadata: {
            ...restOfTemplate.metadata,
            aiChatAnswerFormat: '',
            aiChatPrompt: i18n[language].aiChatPrompt ?? template.metadata.aiChatPrompt,
            aiChatAnswerStates:
              template.metadata?.aiChatAnswerStates?.filter(
                (item) => item.isPositiveHit !== false,
              ) || [],
          },
        }
      : undefined;

    const aiNotificationEventSpotCheck = isEventSpotCheckMode
      ? {
          ...restOfTemplate,
          aiChatAnswerFormat: '',
          aiChatPrompt: i18n[language].aiChatPrompt ?? template.metadata.aiChatPrompt,
          metadata: {
            ...restOfTemplate.metadata,
            aiChatAnswerFormat: '',
            title: i18n[language].title ?? template.title,
            aiChatAnswerStates:
              template.metadata?.aiChatAnswerStates?.filter(
                (item) => item.isPositiveHit !== false,
              ) || [],
          },
        }
      : undefined;
    setSpotCheck(scheduledActivitySpotCheck as any);
    setEventSpotCheck(aiNotificationEventSpotCheck as any);
    setView('spot-check-form');
    setFormMode('create');
    setIsExistingSpotCheck(false);
  };

  const submitSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (spotCheckMode === 'event') {
      setEventSpotCheckSearchPrompt(spotCheckSearch);
    } else {
      setSpotCheckSearchPrompt(spotCheckSearch);
    }
    setView('spot-check-form');
    setFormMode('create');
    setIsExistingSpotCheck(false);
  };

  return (
    <div className={styles.spotCheckTemplates}>
      <div className={styles.spotCheckTemplatesContent}>
        <div className={styles.spotCheckTemplatesCustom}>
          <form className={styles.inputContainer} onSubmit={submitSearch}>
            <input
              type="text"
              placeholder={locale.scheduledAIChat.structuredOutputInput}
              value={spotCheckSearch}
              onChange={handleSpotCheckSearch}
            />
            <button type="submit" className={styles.searchButton}>
              <MDIIcon icon={ArrowSearch} size={18} fill="var(--color-white)" />
            </button>
          </form>
        </div>
        <div className={styles.spotCheckTemplatesListWrapper}>
          <h2>{title}</h2>
          <div className={styles.spotCheckTemplatesList}>
            {templates.map((template) => (
              <SpotCheckCard
                key={`${selectedCategoryKey}-${template.id}`}
                title={template.i18n[language].title ?? template.title}
                onClick={() => handleTemplateClick(template)}
                templateThumbnail={`${settings.publicUrl}/${template.metadata?.templateThumbnail}`}
                description={template.i18n[language].description ?? template.description}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
